import React from 'react';
import type { FeedbackItem, FeedbackVoteValue } from '../../lib/feedback';

interface FeedbackCardProps {
  item: FeedbackItem;
  userVote: FeedbackVoteValue | 0;
  onVote: (feedbackId: string, desired: FeedbackVoteValue) => void;
  isVoting?: boolean;
}

const formatTimeAgo = (timestamp: FeedbackItem['createdAt']) => {
  if (!timestamp || typeof timestamp.toMillis !== 'function') {
    return 'just now';
  }

  const diffMs = Date.now() - timestamp.toMillis();
  const seconds = Math.max(1, Math.floor(diffMs / 1000));
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return `${seconds}s ago`;
};

const FeedbackCard: React.FC<FeedbackCardProps> = ({
  item,
  userVote,
  onVote,
  isVoting = false,
}) => {
  const timeAgo = formatTimeAgo(item.createdAt);

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Anonymous Creator
      </div>
      <p className="mt-2 whitespace-pre-wrap text-sm text-slate-900">
        {item.text}
      </p>
      <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onVote(item.id, 1)}
            disabled={isVoting}
            aria-pressed={userVote === 1}
            className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 transition ${
              userVote === 1
                ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:text-slate-800'
            }`}
          >
            ▲
          </button>
          <span className="text-sm font-semibold text-slate-800">
            {item.score}
          </span>
          <button
            type="button"
            onClick={() => onVote(item.id, -1)}
            disabled={isVoting}
            aria-pressed={userVote === -1}
            className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 transition ${
              userVote === -1
                ? 'border-rose-400 bg-rose-50 text-rose-700'
                : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:text-slate-800'
            }`}
          >
            ▼
          </button>
        </div>
        <span>{timeAgo}</span>
      </div>
    </div>
  );
};

export default FeedbackCard;
